import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, MessageCircle } from 'lucide-react';
import { portalApi } from '../lib/api';

interface ThreadSummary {
  id: string;
  subject: string | null;
  lastMessageAt: string | null;
  lastContactReadAt: string | null;
  isArchived: boolean;
}

interface MessageItem {
  id: string;
  direction: string;
  senderUserName: string | null;
  senderContactName: string | null;
  body: string;
  isAutomated: boolean;
  createdAt: string;
}

interface ThreadDetail {
  id: string;
  subject: string | null;
  messages: MessageItem[];
}

function timeAgo(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('portal.now');
  if (minutes < 60) return t('portal.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('portal.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('portal.daysAgo', { count: days });
  return new Date(dateStr).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' });
}

export function MessagesPage({ refreshKey }: { refreshKey?: number }) {
  const { t } = useTranslation();
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [threadDetail, setThreadDetail] = useState<ThreadDetail | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load threads
  useEffect(() => {
    setLoading(true);
    portalApi
      .getThreads()
      .then((res: { data: ThreadSummary[] }) => setThreads(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [refreshKey]);

  // Load thread detail
  useEffect(() => {
    if (!selectedThreadId) {
      setThreadDetail(null);
      return;
    }
    portalApi
      .getThread(selectedThreadId)
      .then((res: ThreadDetail) => {
        setThreadDetail(res);
        // Mark as read
        portalApi.markThreadRead(selectedThreadId).catch(() => {});
      })
      .catch(() => {});
  }, [selectedThreadId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [threadDetail?.messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedThreadId || sending) return;
    setSending(true);
    try {
      await portalApi.sendMessage(selectedThreadId, newMessage.trim());
      setNewMessage('');
      // Reload thread
      const res = await portalApi.getThread(selectedThreadId);
      setThreadDetail(res);
    } catch {
      // ignore
    } finally {
      setSending(false);
    }
  };

  // Thread detail view
  if (selectedThreadId && threadDetail) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-card">
          <button
            onClick={() => setSelectedThreadId(null)}
            className="text-primary text-sm"
          >
            &larr;
          </button>
          <span className="text-sm font-medium truncate">
            {threadDetail.subject || t('portal.messages')}
          </span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {threadDetail.messages.map((msg) => {
            const isFromClient = msg.direction === 'CLIENT_TO_OFFICE';
            const senderName = msg.senderUserName || msg.senderContactName || (msg.isAutomated ? t('portal.automated') : t('portal.fromOffice'));

            if (msg.isAutomated) {
              return (
                <div key={msg.id} className="flex justify-center">
                  <div className="bg-muted rounded-[12px] px-3 py-2 max-w-[85%] text-center">
                    <p className="text-xs text-muted-foreground mb-0.5">{senderName}</p>
                    <p className="text-sm text-foreground">{msg.body}</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">{timeAgo(msg.createdAt, t)}</p>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={msg.id}
                className={`flex ${isFromClient ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`rounded-[12px] px-3 py-2 max-w-[80%] ${
                    isFromClient
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  <p className={`text-xs mb-0.5 ${isFromClient ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    {senderName}
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                  <p className={`text-xs mt-1 ${isFromClient ? 'text-primary-foreground/50' : 'text-muted-foreground/60'}`}>
                    {timeAgo(msg.createdAt, t)}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Compose */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-border bg-card">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={t('portal.writeMessage')}
            className="flex-1 text-sm border border-border/60 rounded-full px-4 py-3 bg-surface-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className="gradient-primary shadow-lifted active:scale-95 text-primary-foreground rounded-full w-11 h-11 flex items-center justify-center disabled:opacity-50 transition-transform"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Thread list view
  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold mb-3">{t('portal.messages')}</h2>
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-muted rounded-lg h-16 animate-pulse" />
          ))}
        </div>
      ) : threads.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-14 h-14 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="h-7 w-7 text-muted-foreground/40" />
          </div>
          <p className="text-sm text-muted-foreground">{t('portal.noMessages')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map((thread) => {
            const hasUnread =
              thread.lastMessageAt &&
              (!thread.lastContactReadAt || new Date(thread.lastMessageAt) > new Date(thread.lastContactReadAt));
            return (
              <button
                key={thread.id}
                onClick={() => setSelectedThreadId(thread.id)}
                className={`w-full text-left rounded-[12px] border border-border p-3 transition-colors ${
                  hasUnread ? 'bg-brand-soft border-primary/20' : 'bg-card'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">
                    {thread.subject || t('portal.messages')}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {hasUnread ? (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    ) : null}
                    {thread.lastMessageAt ? (
                      <span className="text-xs text-muted-foreground">
                        {timeAgo(thread.lastMessageAt, t)}
                      </span>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
